'use client';

import React from 'react';
import dynamic from 'next/dynamic';
import { ExperienceType } from '../types';
import {
  ActivityWithDay,
  Location,
  NearByPublicTravels,
  attractions,
} from '@/types/itineraryTypes';

const MapWithActivityList = dynamic(
  () => import('@/components/packages/MapWithActivityList'),
  { ssr: false }
);

interface ExperienceMapProps {
  experience: unknown;
  type: ExperienceType;
}

export const ExperienceMap: React.FC<ExperienceMapProps> = ({
  experience,
  type,
}) => {
  if (!experience || type !== 'activity') return null;

  const exp = experience as {
    activities?: Array<{
      time?: number | string;
      startTime?: string;
      endTime?: string;
      title?: string;
      description?: string;
      cost?: string | number;
      icon?: string;
      day?: string;
      imageUrls?: string[];
      notes?: unknown[];
      targetCurrencyRate?: string | number;
      locations?: {
        pincode?: string;
        name?: string;
        description?: string;
        type?: string;
        isBusiness?: boolean;
        isRegion?: boolean;
        cost?: string | number;
        district?: string;
        city?: string;
        state?: string;
        rating?: string | number;
        reviews?: string | number;
        coordinates?: {
          lat?: number;
          lng?: number;
          coordinates?: [number, number];
        };
        nearByPublicTravels?: unknown;
        attractions?: unknown;
      };
    }>;
  };

  if (!Array.isArray(exp?.activities) || exp.activities.length === 0) {
    return null;
  }

  // Helper function to convert time string to minutes for sorting
  const timeStringToMinutes = (timeStr: string) => {
    if (!timeStr) return 0;
    const [time, modifier] = timeStr.split(' ');
    const [hoursStr, minutesStr] = time.split(':');
    let hours = Number(hoursStr);
    const minutes = Number(minutesStr);
    if (modifier === 'PM' && hours !== 12) hours += 12;
    if (modifier === 'AM' && hours === 12) hours = 0;
    return hours * 60 + minutes;
  };

  // Convert activities to ActivityWithDay format
  const activitiesWithDay: ActivityWithDay[] = exp.activities
    .map(activity => ({
      time:
        typeof activity.time === 'number'
          ? activity.time >= 60
            ? `${Math.floor(activity.time / 60)} hr${
                Math.floor(activity.time / 60) > 1 ? 's' : ''
              }${
                activity.time % 60 !== 0
                  ? ` ${activity.time % 60} min${
                      activity.time % 60 > 1 ? 's' : ''
                    }`
                  : ''
              }`
            : `${activity.time} min${activity.time > 1 ? 's' : ''}`
          : String(activity.time || ''),
      startTime: activity.startTime || '',
      endTime: activity.endTime || '',
      title: activity.title || '',
      description: activity.description || '',
      cost: String(activity.cost || ''),
      icon: activity.icon || '',
      day: activity.day || '1',
      imageUrls: activity.imageUrls || [],
      notes: Array.isArray(activity.notes)
        ? activity.notes.filter((n): n is string => typeof n === 'string')
        : [],
      targetCurrencyRate: String(activity.targetCurrencyRate || ''),
      locations: activity.locations
        ? [
            {
              imageUrls: activity.imageUrls || [],
              pincode: activity.locations.pincode || '',
              name: activity.locations.name || '',
              description: activity.locations.description || '',
              type: activity.locations.type || '',
              isBusiness: !!activity.locations.isBusiness,
              isRegion: !!activity.locations.isRegion,
              cost: String(activity.locations.cost || ''),
              district: activity.locations.district || '',
              city: activity.locations.city || '',
              state: activity.locations.state || '',
              rating: String(activity.locations.rating ?? ''),
              reviews: String(activity.locations.reviews ?? ''),
              coordinates: (() => {
                const coords = activity.locations.coordinates;
                if (coords?.lat && coords?.lng) {
                  return { lat: coords.lat, lng: coords.lng };
                }
                if (
                  Array.isArray(coords?.coordinates) &&
                  coords.coordinates.length >= 2
                ) {
                  const [lng, lat] = coords.coordinates;
                  return { lat: Number(lat) || 0, lng: Number(lng) || 0 };
                }
                return { lat: 0, lng: 0 };
              })(),
              nearByPublicTravels: activity.locations.nearByPublicTravels as
                | NearByPublicTravels
                | undefined,
              attractions: activity.locations.attractions as
                | attractions
                | undefined,
            } as Location,
          ]
        : [],
    }))
    .sort((a, b) => {
      if (a.startTime && b.startTime) {
        return (
          timeStringToMinutes(a.startTime) - timeStringToMinutes(b.startTime)
        );
      }
      return 0;
    });

  if (activitiesWithDay.length === 0) return null;

  return <MapWithActivityList activities={activitiesWithDay} type={type} />;
};
